import React, { useMemo } from 'react';
import Icon from 'react-icons-kit';
import { plus } from 'react-icons-kit/ikons/plus';
import { refresh } from 'react-icons-kit/ionicons/refresh';
import { getAllQbClassesForTable } from '../../../requests/QbClassRequests';
import Modal from 'react-modal';
import MinuteTable from '../../components/MinuteTable';
import { ic_mode_edit } from 'react-icons-kit/md/ic_mode_edit';
import ConfirmationModal from '../../components/ConfirmationModal';
import '../../../css/Timesheets.css';
import Select from '../../components/Select.js';

try {
    Modal.setAppElement('#root');
} catch {}

const QbClassResourceList = ({
    title,
    onClick,
    history,
    createNewLabel,
    createNewPath,
    activateQbClass,
    deactivateQbClass,
    showToast,
}) => {
    const statusFilters = [
        { name: 'All Classes', id: 'all' },
        { name: 'ACTIVE', id: 'active' },
        { name: 'INACTIVE', id: 'inactive' },
    ];

    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [refreshTable, setRefreshTable] = React.useState(false);
    const [filter, setFilter] = React.useState('active');
    const [resetTable, setResetTable] = React.useState(false);
    const [updatingStatusIds, setUpdatingStatusIds] = React.useState(new Set());
    const [pageCount, setPageCount] = React.useState(0);
    const fetchIdRef = React.useRef(0);
    const sortIdRef = React.useRef(0);

    // Confirmation modal state
    const [confirmationModalIsOpen, setConfirmationModalIsOpen] = React.useState(false);
    const [confirmationModalTitle, setConfirmationModalTitle] = React.useState('');
    const [confirmationModalMessage, setConfirmationModalMessage] = React.useState('');
    const [confirmationModalOnConfirm, setConfirmationModalOnConfirm] = React.useState(null);
    const [
        confirmationModalConfirmActionLabel,
        setConfirmationModalConfirmActionLabel,
    ] = React.useState('');
    const [
        confirmationModalConfirmActionIsDestructive,
        setConfirmationModalConfirmActionIsDestructive,
    ] = React.useState(true);

    const fetchData = React.useCallback(({ sortBy, pageSize, pageIndex, filter }) => {
        const fetchId = ++fetchIdRef.current;
        setLoading(true);

        setTimeout(() => {
            if (fetchId === fetchIdRef.current) {
                let paramsObject = {
                    current_page: pageIndex + 1,
                    per_page: pageSize,
                    filter: filter,
                };
                Promise.all([getAllQbClassesForTable(paramsObject)])
                    .then(response => {
                        let json = response[0];
                        console.log('QB Classes API Response:', json);
                        let qbClasses = json.qbClassesList;
                        console.log('QB Classes List:', qbClasses);
                        if (sortBy.length > 0) {
                            const sortId = ++sortIdRef.current;
                            if (sortId === sortIdRef.current) {
                                let sorted = qbClasses;
                                sorted.sort((a, b) => {
                                    for (let i = 0; i < sortBy.length; ++i) {
                                        if (a[sortBy[i].id] > b[sortBy[i].id])
                                            return sortBy[i].desc ? -1 : 1;
                                        if (a[sortBy[i].id] < b[sortBy[i].id])
                                            return sortBy[i].desc ? 1 : -1;
                                    }
                                    return 0;
                                });
                                setData(qbClasses);
                                setPageCount(Math.ceil(json.meta.total / pageSize));
                                setLoading(false);
                            }
                        } else {
                            setData(qbClasses);
                            setPageCount(Math.ceil(json.meta.total / pageSize));
                            setLoading(false);
                        }
                    })
                    .catch(error => {
                        console.error('Error fetching QB Classes:', error);
                        console.error('Error details:', error.response || error);
                        setLoading(false);
                        setData([]);
                    });
            }
        }, 1000);
    }, []);

    const onToggleApproved = qbClass => {
        // Prevent multiple clicks on the same item
        if (updatingStatusIds.has(qbClass.id)) {
            return;
        }

        setUpdatingStatusIds(prev => new Set(prev).add(qbClass.id));

        if (qbClass.hidden === 'No') {
            deactivateQbClass(qbClass.id);
        } else {
            activateQbClass(qbClass.id);
        }

        // Remove the updating status after a shorter delay since we have event-based refresh
        setTimeout(() => {
            setUpdatingStatusIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(qbClass.id);
                return newSet;
            });
        }, 1000);
    };

    const showConfirmationModal = (qbClass, action) => {
        // Prevent showing modal if already updating
        if (updatingStatusIds.has(qbClass.id)) {
            return;
        }

        const isDeactivating = action === 'deactivate';

        setConfirmationModalTitle(isDeactivating ? 'Deactivate Class' : 'Activate Class');
        setConfirmationModalMessage(
            isDeactivating
                ? `Are you sure you want to deactivate this class?`
                : `Are you sure you want to activate this class?`
        );
        setConfirmationModalConfirmActionLabel(isDeactivating ? 'Deactivate' : 'Activate');
        setConfirmationModalConfirmActionIsDestructive(isDeactivating);
        setConfirmationModalOnConfirm(() => () => {
            onToggleApproved(qbClass);
            setConfirmationModalIsOpen(false);
        });
        setConfirmationModalIsOpen(true);
    };

    const handleConfirmationModalRequestedClose = () => {
        setConfirmationModalIsOpen(false);
    };

    const handleFilterChanged = value => {
        setFilter(value);
        setRefreshTable(!refreshTable);
    };

    const columns = useMemo(
        () => [
            {
                accessor: 'name',
                Header: 'Class Name',
            },
            {
                Header: 'Status',
                id: 'status',
                accessor: () => 'Status',
                Cell: tableProps => {
                    const isUpdating = updatingStatusIds.has(tableProps.row.original.id);
                    return (
                        <td className="tsStatusCell">
                            {tableProps.row.original.hidden === 'No' && (
                                <span
                                    className="tsStatus tsStatusApproved"
                                    onClick={() =>
                                        showConfirmationModal(tableProps.row.original, 'deactivate')
                                    }
                                    style={{ cursor: isUpdating ? 'default' : 'pointer' }}
                                >
                                    {isUpdating ? 'Updating...' : 'Active'}
                                </span>
                            )}
                            {tableProps.row.original.hidden === 'Yes' && (
                                <span
                                    className="tsStatus tsStatusPending"
                                    onClick={() =>
                                        showConfirmationModal(tableProps.row.original, 'activate')
                                    }
                                    style={{ cursor: isUpdating ? 'default' : 'pointer' }}
                                >
                                    {isUpdating ? 'Updating...' : 'Inactive'}
                                </span>
                            )}
                        </td>
                    );
                },
            },
            {
                Header: 'Actions',
                id: 'actions',
                accessor: () => 'Actions',
                Cell: tableProps => (
                    <Icon
                        onClick={() => onClick && onClick(tableProps.row.original)}
                        icon={ic_mode_edit}
                        className="tableIcon asAccountUserIcon"
                        size={22}
                        data-tip="Edit Class"
                    />
                ),
            },
        ],
        [updatingStatusIds, showConfirmationModal]
    );

    React.useEffect(
        () => {
            setRefreshTable(!refreshTable);
        },
        [filter]
    );

    // Listen for saga success actions to refresh table
    React.useEffect(() => {
        const handleStorageChange = () => {
            // Force table refresh when Redux store updates
            setRefreshTable(prev => !prev);
        };

        // Listen for custom events that might be dispatched by sagas
        window.addEventListener('qbclass-status-updated', handleStorageChange);

        return () => {
            window.removeEventListener('qbclass-status-updated', handleStorageChange);
        };
    }, []);

    return (
        <div>
            <h3 className="cardHeading">
                {title}
                <div className="bodyHeader filters">
                    <div className="" style={{ fontWeight: 'lighter' }}>
                        <Select
                            toggleStyle="button"
                            className="multiselectFilter"
                            options={statusFilters}
                            value={filter}
                            defaultValue="active"
                            onChange={handleFilterChanged}
                        />
                    </div>

                    {createNewPath && (
                        <div className="">
                            <button
                                className="secondaryButton"
                                style={{ height: '35px' }}
                                onClick={() => history.push(createNewPath)}
                            >
                                <Icon className="icon" icon={plus} size={20} />
                                {createNewLabel}
                            </button>
                        </div>
                    )}
                    <div className="headerActions">
                        <button
                            className="secondaryButton"
                            style={{ height: '35px' }}
                            onClick={() => setRefreshTable(!refreshTable)}
                        >
                            <Icon className="icon" icon={refresh} size={20} />
                        </button>
                    </div>
                </div>
            </h3>
            <div className="cardContainer asSection">
                <MinuteTable
                    columns={columns}
                    data={data}
                    fetchData={fetchData}
                    loading={loading}
                    pageCount={pageCount}
                    filter={filter}
                    refresh={refreshTable}
                    resetTable={resetTable}
                />
            </div>
            <ConfirmationModal
                isOpen={confirmationModalIsOpen}
                title={confirmationModalTitle}
                message={confirmationModalMessage}
                onConfirm={confirmationModalOnConfirm}
                confirmActionLabel={confirmationModalConfirmActionLabel}
                confirmActionIsDestructive={confirmationModalConfirmActionIsDestructive}
                onRequestClose={handleConfirmationModalRequestedClose}
            />
        </div>
    );
};

export default QbClassResourceList;
